/* eslint-disable @next/next/no-img-element */
import { Logo, PrefetchLink, SocialIcons, type SocialIconsProps, TwentyFourSocial } from '@/ui';
import { APIImageType, Layout, RepeaterLink, SocialWithContact } from '@types';
import { clsx } from 'clsx';

import { imageMetaConverter } from '@/lib/server';

import { BottomFooter } from './bottom-footer';
import { QuickLinks } from './quick-links';
import { SiteInfo } from './site-info';

export interface FooterProps extends React.ComponentPropsWithoutRef<'footer'> {
  variant?: Layout['footer'];
  /** Appears above the social icons in SiteInfo */
  logo: APIImageType | Array<APIImageType>;
  /** Appears in the copyright section next to 'Operated By' */
  operatorLogo?: APIImageType | Array<APIImageType> | undefined;
  operatorHref?: string;
  links: RepeaterLink[];
  address?: string;
  siteName: string;
  displayName?: string;
  exitButton?: boolean;
  hideSiteName?: boolean;
  socials?: SocialWithContact;
  directionsLink?: string;
  contactLink?: string;
  policyLinks?: RepeaterLink[];
  componentProps?: {
    socialIcons?: Partial<SocialIconsProps>;
  };
}

export function Footer({ variant = 'detailedWithBackground', ...props }: FooterProps) {
  if (variant?.includes('Background')) {
    props.className = clsx(props.className, 'bg-card text-card-foreground');
  }

  switch (variant) {
    case 'detailed':
    case 'detailedWithBackground':
      return <Detailed {...props} />;
    case 'compact':
    case 'compactWithBackground':
      return <Compact {...props} />;
    default:
      return <Simple {...props} />;
  }
}

export function Detailed({
  className,
  siteName,
  children,
  componentProps,
  displayName,
  exitButton,
  operatorLogo,
  links,
  logo,
  address,
  hideSiteName,
  socials,
  policyLinks,
  operatorHref,
}: FooterProps) {
  return (
    <footer className={clsx('z-20 flex w-full flex-col items-center', className)}>
      <div
        className={clsx(
          'max-w-[theme(screens.3xl)] grid w-full grid-cols-1 gap-12 px-4 py-12 lg:grid-cols-3 lg:justify-items-center lg:gap-6 lg:px-6',
          { 'items-center': children },
        )}
      >
        <SiteInfo
          exitButton={exitButton}
          componentProps={componentProps}
          siteName={siteName}
          address={address}
          logo={logo}
          displayName={displayName}
          hideSiteName={hideSiteName}
          socials={socials}
        />

        {children && children}

        {Boolean(links.length) && <QuickLinks links={links} title={'Quick Links'} />}

        {Boolean(policyLinks?.length) && !children && (
          <QuickLinks links={policyLinks || []} title={'More Information'} />
        )}
      </div>

      <div
        className={clsx('text-muted-foreground flex min-h-16 w-full items-center justify-center', {
          'bg-muted': children,
        })}
      >
        <BottomFooter
          operatorLogo={operatorLogo}
          operatorHref={operatorHref}
          policyLinks={children ? policyLinks : undefined}
          siteName={siteName}
        />
      </div>
    </footer>
  );
}

export function Compact({ className, address, socials, siteName, operatorLogo, operatorHref }: FooterProps) {
  const year = new Date().getFullYear();
  const hasSocials = Boolean(Object.values(socials || {}).filter(Boolean).length);
  const operatorLogoData = imageMetaConverter(operatorLogo);

  return (
    <footer
      className={clsx(
        'max-w-[theme(screens.3xl)] text-muted-foreground flex w-full flex-col items-center gap-3 px-4 py-4 text-center text-xs font-medium md:flex-row md:items-center md:gap-8 md:px-8 md:text-left',
        className,
        { 'justify-between': hasSocials },
        { 'justify-center': !hasSocials },
      )}
    >
      {hasSocials && <SocialIcons address={address} links={{ ...socials }} orientation={'horizontal'} />}

      <div className={'flex flex-col items-baseline gap-3 sm:flex-row'}>
        <span>
          &copy; Copyright {year} {siteName}. All rights reserved.{' '}
        </span>
        <div className="flex flex-1 flex-wrap items-center gap-3">
          {operatorLogoData?.src && operatorHref && (
            <PrefetchLink href={operatorHref}>
              Operated by
              <img src={operatorLogoData.src} alt={operatorLogoData.alt} className="ml-2 inline h-4" />
            </PrefetchLink>
          )}
          <span className={'border-border h-4 w-0 border-r'} />
          <PrefetchLink href={'https://www.24social.io/'}>
            Powered by
            <TwentyFourSocial className="ml-2 inline" />
          </PrefetchLink>
        </div>
      </div>
    </footer>
  );
}

export function Simple({ className, address, socials, siteName, logo, links, ...props }: FooterProps) {
  return (
    <div className="flex flex-col gap-6">
      <footer className={clsx('z-20 flex w-full flex-col items-center gap-8', className)}>
        <Logo logo={logo} siteName={siteName} href={'/'} />
        <QuickLinks links={links} />
      </footer>

      <Compact {...{ className, address, socials, siteName, logo, links, ...props }} />
    </div>
  );
}
